import { PriceHistoryEntry } from '../../_types/imported-listing'
import { SparklineGeometry } from './types'

const VERTICAL_PADDING = 6

/**
 * Filters price-history entries down to the ones with a real price, then
 * builds the SVG path data for the sparkline plus a matching filled-area
 * path so the component can render both layers without re-walking the
 * series.
 *
 * @param {PriceHistoryEntry[]} entries - the raw price-history entries (may include nulls)
 * @param {number} width - the SVG viewport width in px
 * @param {number} height - the SVG viewport height in px
 * @returns {SparklineGeometry} pre-computed paths plus trend metadata
 */
const buildSparklineGeometry = (
  entries: PriceHistoryEntry[],
  width: number,
  height: number,
): SparklineGeometry => {
  const validEntries = entries.filter(
    entry => typeof entry.priceMonthly === 'number',
  )

  if (validEntries.length === 0) {
    return {
      pathData: '',
      areaData: '',
      endpointX: 0,
      endpointY: 0,
      trendDirection: 'flat',
      hasData: false,
      minPrice: 0,
      maxPrice: 0,
      firstPrice: 0,
      lastPrice: 0,
    }
  }

  const prices = validEntries.map(entry => entry.priceMonthly as number)
  const minPrice = Math.min(...prices)
  const maxPrice = Math.max(...prices)
  const range = Math.max(maxPrice - minPrice, 1)

  const firstObserved = validEntries[0].observedAt
  const lastObserved = validEntries[validEntries.length - 1].observedAt
  const timeRange = Math.max(lastObserved - firstObserved, 1)

  const usableHeight = height - 2 * VERTICAL_PADDING
  const points = validEntries.map(entry => {
    const xRatio =
      validEntries.length === 1
        ? 0.5
        : (entry.observedAt - firstObserved) / timeRange
    const yRatio = ((entry.priceMonthly as number) - minPrice) / range

    return {
      x: xRatio * width,
      y: VERTICAL_PADDING + (1 - yRatio) * usableHeight,
    }
  })

  const lineSegments = points
    .map(
      (point, index) =>
        `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`,
    )
    .join(' ')

  const areaSegments = `${lineSegments} L ${points[points.length - 1].x.toFixed(2)} ${height} L ${points[0].x.toFixed(2)} ${height} Z`

  const firstPrice = validEntries[0].priceMonthly as number
  const lastPrice = validEntries[validEntries.length - 1].priceMonthly as number

  let trendDirection: 'up' | 'down' | 'flat' = 'flat'
  if (lastPrice > firstPrice) {
    trendDirection = 'up'
  } else if (lastPrice < firstPrice) {
    trendDirection = 'down'
  }

  return {
    pathData: lineSegments,
    areaData: areaSegments,
    endpointX: points[points.length - 1].x,
    endpointY: points[points.length - 1].y,
    trendDirection,
    hasData: true,
    minPrice,
    maxPrice,
    firstPrice,
    lastPrice,
  }
}

export { buildSparklineGeometry }
